/**
 * Web addresses in a note's text, as links (design 45 §15.4): the text split into plain runs and
 * addresses, for a screen to draw as React text and anchors — never HTML, so nothing in a note can
 * become markup. Only `http://` and `https://` addresses are links; a mail address, `www.` alone, a
 * `javascript:` or any other scheme stays text. An address ends at white space, a quote or an angle
 * bracket; punctuation that closes the sentence around it — `.`, `,`, `;`, `:`, `!`, `?`, a quote, a
 * closing bracket the address did not open — is left outside it.
 */

export type TextPart = { readonly kind: 'text'; readonly text: string } | { readonly kind: 'link'; readonly text: string; readonly href: string };

const ADDRESS = /\bhttps?:\/\/[^\s<>"'`]+/gi;

const TRAILING = new Set(['.', ',', ';', ':', '!', '?', "'", '"', '*']);

const CLOSERS: Readonly<Record<string, string>> = { ')': '(', ']': '[', '}': '{' };

/** How many of `ch` the text holds. */
function count(text: string, ch: string): number {
  let n = 0;
  for (const c of text) if (c === ch) n += 1;
  return n;
}

/** The address with the sentence's punctuation taken off its end: a bracket only when the address holds fewer openers than closers. */
function trimEnd(address: string): string {
  let end = address.length;
  for (;;) {
    const last = address[end - 1];
    if (last === undefined) return '';
    const opener = CLOSERS[last];
    const head = address.slice(0, end);
    if (TRAILING.has(last) || (opener !== undefined && count(head, last) > count(head, opener))) end -= 1;
    else return head;
  }
}

/** The text in order as plain runs and links; empty for empty text. A link's `href` is exactly its text. */
export function linkify(text: string): readonly TextPart[] {
  const parts: TextPart[] = [];
  let from = 0;
  for (const match of text.matchAll(ADDRESS)) {
    const start = match.index;
    const address = trimEnd(match[0]);
    // A scheme with nothing after it is not an address.
    if (/^https?:\/\/$/i.test(address)) continue;
    if (start > from) parts.push({ kind: 'text', text: text.slice(from, start) });
    parts.push({ kind: 'link', text: address, href: address });
    from = start + address.length;
  }
  if (from < text.length) parts.push({ kind: 'text', text: text.slice(from) });
  return parts;
}
